import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Check, Copy, FileSearch, X } from 'lucide-react';
import { appToast as toast } from '@/lib/app-toast';
import { isAxiosError } from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PdfPreviewCard } from '@/pages/exception-detail/components';
import { useDraftApprove, useDraftReject, useDrafts } from '@/features/drafts/hooks';
import type { DraftRow } from '@/api/cockpit';
import { formatRelativeTime } from '@/lib/utils';

function formatCurrency(value: number | null): string {
  if (value === null) return 'Pending total';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
}

function SalesOrderCell({ value }: { value: string | null }): JSX.Element {
  async function copy(): Promise<void> {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`Copied ${value}`);
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-medium">{value ?? 'Pending generation'}</span>
      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={copy}
          aria-label={`Copy ${value} to clipboard`}
          title="Copy to clipboard"
          className="h-6 w-6"
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </span>
  );
}

export function DraftsPage(): JSX.Element {
  const navigate = useNavigate();
  const [deliveryCorrectionTarget, setDeliveryCorrectionTarget] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const includeApproved = searchParams.get('includeApproved') === 'true';
  const { data, isLoading, isError, error, refetch, isFetching } = useDrafts({
    includeApproved,
  });
  const approve = useDraftApprove();
  const reject = useDraftReject();
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [paymentSelections, setPaymentSelections] = useState<Record<string, string>>({});
  const [paymentOverrideReasons, setPaymentOverrideReasons] = useState<Record<string, string>>({});
  const [approving, setApproving] = useState<DraftRow | null>(null);
  const [approvalShippingDate, setApprovalShippingDate] = useState('');
  const [approvalDeliveryDate, setApprovalDeliveryDate] = useState('');
  const [shippingDateConfirmed, setShippingDateConfirmed] = useState(false);
  const draftFilter = searchParams.get('draft')?.trim() ?? '';
  const visibleDrafts = useMemo(() => {
    if (!data) return [];
    if (!draftFilter) return data;
    return data.filter((draft) => draft.id === draftFilter);
  }, [data, draftFilter]);
  function setIncludeApproved(next: boolean): void {
    const params = new URLSearchParams(searchParams);
    if (next) {
      params.set('includeApproved', 'true');
    } else {
      params.delete('includeApproved');
    }
    setSearchParams(params);
  }
  function clearDraftFilter(): void {
    const params = new URLSearchParams(searchParams);
    params.delete('draft');
    setSearchParams(params);
  }
  function openApproval(draft: DraftRow): void {
    setApproving(draft);
    setApprovalShippingDate(draft.requestedShippingDate ?? '');
    setApprovalDeliveryDate(draft.requestedDeliveryDate ?? '');
    setShippingDateConfirmed(false);
  }
  const approvalDateError =
    getApprovalDateError(approvalShippingDate, approvalDeliveryDate) ??
    (!approvalDeliveryDate &&
    approving?.requestedDateReviewWarnings?.some(
      (warning) => warning.fieldPath === 'requestedDeliveryDate',
    )
      ? 'Customer Care must enter the clarified Requested Delivery Date before approval.'
      : null);
  const approvalDateWarnings = getApprovalDateWarnings(approvalShippingDate, approvalDeliveryDate);
  const deliveryReview = approving?.deliveryReview;
  const deliveryApprovalBlocked = Boolean(deliveryReview && !deliveryReview.terms.valid);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Drafts awaiting approval</h1>
          <p className="text-sm text-muted-foreground">
            Approve to create the Dynamics 365 sales order. Reject to send the row back to the
            operator with a reason.
          </p>
        </div>
        <label className="flex items-center gap-2 rounded-md border bg-muted/20 px-3 py-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={includeApproved}
            onChange={(event) => setIncludeApproved(event.target.checked)}
          />
          <span>Show approved drafts</span>
        </label>
      </div>

      {isLoading && <Skeleton className="h-40 w-full" />}

      {!isLoading && isError ? (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-8">
            <div className="text-sm text-muted-foreground">
              Failed to load drafts. {error instanceof Error ? error.message : 'Unknown error.'}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <div className="space-y-3">
        {draftFilter ? (
          <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Showing the selected order draft.</span>
            <Button variant="ghost" size="sm" onClick={clearDraftFilter}>
              Show all drafts
            </Button>
          </div>
        ) : null}
        {visibleDrafts.map((draft) => {
          const isAwaitingApproval = draft.state === 'Awaiting Operator Approval';
          const d365Terms =
            draft.d365ResolvedPaymentTermsCode ?? draft.customerDefaultPaymentTermsCode;
          const selectedPaymentTerms = paymentSelections[draft.id] ?? d365Terms ?? '';
          const paymentOverrideActive = Boolean(
            selectedPaymentTerms &&
              (!d365Terms ||
                selectedPaymentTerms.trim().toLowerCase() !== d365Terms.trim().toLowerCase()),
          );
          const paymentOverrideReason = paymentOverrideReasons[draft.id] ?? '';
          const availablePaymentOptions = draft.paymentTermsOptions ?? [];
          const paymentOptions = availablePaymentOptions.some(
            (option) => option.value === selectedPaymentTerms,
          )
            ? availablePaymentOptions
            : selectedPaymentTerms
              ? [
                  { value: selectedPaymentTerms, label: `${selectedPaymentTerms} - D365 default` },
                  ...availablePaymentOptions,
                ]
              : availablePaymentOptions;
          return (
            <Card key={draft.id} className={!isAwaitingApproval ? 'opacity-85' : undefined}>
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base">{draft.customerName}</CardTitle>
                  <CardDescription>
                    PO {draft.poNumber} - {draft.totalLines} lines - ready{' '}
                    {formatRelativeTime(draft.readyAt)}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{formatCurrency(draft.totalValue)}</Badge>
                  <Badge variant="secondary">{draft.state}</Badge>
                </div>
              </CardHeader>
              {draft.paymentTermsMismatch && (
                <CardContent className="pt-0">
                  <div className="flex items-start gap-3 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <div>
                      <div className="font-medium text-amber-900">Payment terms mismatch</div>
                      <div className="text-amber-900/80">
                        PO terms ({draft.paymentTermsCode ?? '-'}) do not match the D365 resolved or
                        default terms ({d365Terms ?? '-'}). Confirm before approval.
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
              <CardContent className="pt-0">
                <Tabs defaultValue="fields" className="w-full">
                  <TabsList>
                    <TabsTrigger value="fields">Campos</TabsTrigger>
                    <TabsTrigger value="pdf">PDF</TabsTrigger>
                  </TabsList>
                  <TabsContent value="fields" className="mt-3">
                    <DraftFieldsView
                      draft={draft}
                      selectedPaymentTerms={selectedPaymentTerms}
                      paymentOptions={paymentOptions}
                      paymentOverrideActive={paymentOverrideActive}
                      paymentOverrideReason={paymentOverrideReason}
                      onPaymentTermsChange={(value) =>
                        setPaymentSelections((current) => ({ ...current, [draft.id]: value }))
                      }
                      onPaymentOverrideReasonChange={(value) =>
                        setPaymentOverrideReasons((current) => ({ ...current, [draft.id]: value }))
                      }
                    />
                  </TabsContent>
                  <TabsContent value="pdf" className="mt-3">
                    <PdfPreviewCard rowId={draft.id} />
                  </TabsContent>
                </Tabs>
              </CardContent>
              {draft.warnings.length > 0 && (
                <CardContent className="pt-0 text-xs text-muted-foreground">
                  <ul className="list-disc pl-4">
                    {draft.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </CardContent>
              )}
              <CardContent className="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/queue/${encodeURIComponent(draft.id)}`}>
                    <FileSearch className="h-4 w-4" />
                    Details
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRejecting(draft.id);
                    setDeliveryCorrectionTarget(null);
                    setReason('');
                  }}
                  disabled={!isAwaitingApproval || reject.isPending}
                >
                  <X className="h-4 w-4" />
                  Reject
                </Button>
                <Button
                  size="sm"
                  onClick={() => openApproval(draft)}
                  disabled={
                    !isAwaitingApproval ||
                    approve.isPending ||
                    (paymentOverrideActive && paymentOverrideReason.trim().length < 8)
                  }
                >
                  <Check className="h-4 w-4" />
                  Approve
                </Button>
              </CardContent>
            </Card>
          );
        })}
        {!isLoading && data && visibleDrafts.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              {draftFilter
                ? 'No matching draft found.'
                : includeApproved
                  ? 'No drafts found.'
                  : 'No drafts to review.'}
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog
        open={rejecting !== null}
        onOpenChange={(open) => {
          if (!open) setRejecting(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject draft</DialogTitle>
            <DialogDescription>
              The reason is written into the staging row history and surfaced to the operator.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this draft not safe to confirm?"
            className="min-h-[120px]"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!rejecting || reason.trim().length < 3) return;
                reject.mutate(
                  { id: rejecting, reason: reason.trim() },
                  {
                    onSuccess: () => {
                      setRejecting(null);
                      if (deliveryCorrectionTarget)
                        navigate(`/exceptions/${deliveryCorrectionTarget}`);
                      setDeliveryCorrectionTarget(null);
                    },
                  },
                );
              }}
              disabled={reason.trim().length < 3 || reject.isPending}
            >
              Confirm reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={approving !== null}
        onOpenChange={(open) => {
          if (!open && !approve.isPending) setApproving(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confirm order details</DialogTitle>
            <DialogDescription>
              Customer Care must confirm the Requested Shipping Date used by CTP. The customer
              Requested Delivery Date remains visible for comparison and is not copied silently.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {deliveryReview ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Delivery defaults: {deliveryReview.customerNumber} ({deliveryReview.dataAreaId})
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed text-left text-sm">
                    <thead>
                      <tr>
                        <th className="pr-2">Field</th>
                        <th className="pr-2">PO</th>
                        <th className="pr-2">D365 default</th>
                        <th>Selected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {([['Delivery Terms', deliveryReview.terms]] as const).map(([label, selection]) => (
                        <tr key={label}>
                          <th className="py-1 pr-2 font-normal">{label}</th>
                          <td className="break-words pr-2">{selection.extractedValue ?? '-'}</td>
                          <td className="break-words pr-2">{selection.defaultValue ?? '-'}</td>
                          <td className="break-words">{selection.value ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {[deliveryReview.terms.message]
                  .filter(Boolean)
                  .map((message) => (
                    <p key={message} className="text-sm text-amber-900">
                      {message}
                    </p>
                  ))}
                {deliveryReview.stale ? (
                  <p className="text-sm text-amber-900">
                    Delivery defaults are outdated. Last synchronized:{' '}
                    {deliveryReview.syncedAt ?? 'unavailable'}.
                  </p>
                ) : null}
                <Button
                  variant="link"
                  className="h-auto p-0 text-sm"
                  disabled={approve.isPending}
                  onClick={() => {
                    if (!approving) return;
                    setDeliveryCorrectionTarget(approving.id);
                    setRejecting(approving.id);
                    setReason('Delivery Terms require Customer Care correction.');
                    setApproving(null);
                  }}
                >
                  <FileSearch className="h-4 w-4" />
                  Return for delivery correction
                </Button>
              </div>
            ) : null}
            <div className="grid gap-2">
              <Label htmlFor="approval-requested-shipping-date">Requested Shipping Date</Label>
              <Input
                id="approval-requested-shipping-date"
                type="date"
                value={approvalShippingDate}
                disabled={approve.isPending}
                onChange={(event) => {
                  setApprovalShippingDate(event.target.value);
                  setShippingDateConfirmed(false);
                }}
              />
              <p className="text-xs text-muted-foreground">
                Source: customer PO when provided; otherwise Customer Care must enter the date. This
                value is sent to the D365 RequestedShippingDate field.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="approval-requested-delivery-date">Requested Delivery Date</Label>
              <Input
                id="approval-requested-delivery-date"
                type="date"
                value={approvalDeliveryDate}
                disabled={approve.isPending}
                onChange={(event) => {
                  setApprovalDeliveryDate(event.target.value);
                  setShippingDateConfirmed(false);
                }}
              />
              <p className="text-xs text-muted-foreground">
                Optional customer reference. It is sent to D365 as RequestedReceiptDate when present
                and may be changed later in D365 according to the approved process.
              </p>
            </div>
            {approvalDateError ? (
              <p className="text-sm text-destructive">{approvalDateError}</p>
            ) : null}
            {approving?.requestedDateReviewWarnings?.map((warning) => (
              <p key={warning.fieldPath} className="text-sm text-amber-900">
                {warning.message}
              </p>
            ))}
            <p className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-900">
              Check the operational holiday calendar before confirming. Holidays are not detected
              automatically. Customer Care may confirm a holiday date; it will not be blocked or
              moved automatically.
            </p>
            {approvalDateWarnings.length > 0 ? (
              <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-900">
                <div className="font-medium">Date review warning</div>
                <ul className="mt-1 list-disc pl-4">
                  {approvalDateWarnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
                <div className="mt-1 text-xs text-amber-900/80">
                  Customer Care may correct or explicitly confirm the date. The system will not move
                  it automatically.
                </div>
              </div>
            ) : null}
            <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={shippingDateConfirmed}
                disabled={approve.isPending}
                onChange={(event) => setShippingDateConfirmed(event.target.checked)}
              />
              <span>
                I confirm that the Requested Shipping Date is reviewed and is the date CTP should
                run against.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproving(null)} disabled={approve.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (
                  !approving ||
                  approvalDateError ||
                  !shippingDateConfirmed ||
                  deliveryApprovalBlocked
                )
                  return;
                const d365Terms =
                  approving.d365ResolvedPaymentTermsCode ??
                  approving.customerDefaultPaymentTermsCode;
                const selectedPaymentTerms = paymentSelections[approving.id] ?? d365Terms ?? '';
                const paymentOverrideActive = Boolean(
                  selectedPaymentTerms &&
                    (!d365Terms ||
                      selectedPaymentTerms.trim().toLowerCase() !== d365Terms.trim().toLowerCase()),
                );
                const paymentOverrideReason = paymentOverrideReasons[approving.id]?.trim() ?? '';
                approve.mutate(
                  {
                    id: approving.id,
                    requestedShippingDate: approvalShippingDate,
                    requestedDeliveryDate: approvalDeliveryDate || null,
                    shippingDateConfirmed: true,
                    ...(deliveryReview ? { deliveryReviewRevision: deliveryReview.revision } : {}),
                    ...(paymentOverrideActive
                      ? {
                          paymentTermsOverride: selectedPaymentTerms,
                          paymentTermsOverrideReason: paymentOverrideReason,
                        }
                      : {}),
                  },
                  {
                    onSuccess: () => setApproving(null),
                    onError: (error) => {
                      const problem = isAxiosError(error) ? error.response?.data : undefined;
                      const detail = typeof problem?.detail === 'string' ? problem.detail : null;
                      toast.error(detail ?? (error instanceof Error ? error.message : 'Approval failed.'));
                      if (problem?.type === 'invalid_shipping_setup') {
                        setApproving(null);
                        navigate(`/queue/${approving.id}`);
                      }
                    },
                  },
                );
              }}
              disabled={
                Boolean(approvalDateError) ||
                !shippingDateConfirmed ||
                deliveryApprovalBlocked ||
                approve.isPending
              }
            >
              <Check className="h-4 w-4" />
              {approve.isPending ? 'Validating and approving...' : 'Confirm and approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getApprovalDateError(shippingDate: string, deliveryDate: string): string | null {
  if (!shippingDate) return 'Requested Shipping Date is required before approval.';
  if (deliveryDate && shippingDate > deliveryDate) {
    return 'Requested Shipping Date cannot be later than Requested Delivery Date.';
  }
  return null;
}

function getApprovalDateWarnings(shippingDate: string, deliveryDate: string): string[] {
  const warnings: string[] = [];
  const dates = [
    { value: shippingDate, label: 'Requested Shipping Date' },
    { value: deliveryDate, label: 'Requested Delivery Date' },
  ];
  for (const date of dates) {
    if (!date.value) continue;
    const parsed = new Date(`${date.value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) continue;
    if (parsed.getTime() < new Date().setHours(0, 0, 0, 0)) {
      warnings.push(`${date.label} is in the past.`);
    }
    if (parsed.getDay() === 0 || parsed.getDay() === 6) {
      warnings.push(`${date.label} falls on a weekend.`);
    }
  }
  return warnings;
}

function DraftFieldsView({
  draft,
  selectedPaymentTerms,
  paymentOptions,
  paymentOverrideActive,
  paymentOverrideReason,
  onPaymentTermsChange,
  onPaymentOverrideReasonChange,
}: {
  draft: DraftRow;
  selectedPaymentTerms: string;
  paymentOptions: readonly { value: string; label: string }[];
  paymentOverrideActive: boolean;
  paymentOverrideReason: string;
  onPaymentTermsChange: (value: string) => void;
  onPaymentOverrideReasonChange: (value: string) => void;
}): JSX.Element {
  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2">
          <span className="text-muted-foreground">Sales order</span>
          <SalesOrderCell value={draft.salesOrderNumber} />
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2">
          <span className="text-muted-foreground">Ship to</span>
          <span className="text-right font-medium">
            {(draft.shipToName ?? draft.shipToAddress) ? (
              <>
                {draft.shipToName && <div>{draft.shipToName}</div>}
                {draft.shipToAddress && (
                  <div className="text-xs font-normal text-muted-foreground">
                    {draft.shipToAddress}
                  </div>
                )}
              </>
            ) : (
              'Unavailable'
            )}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2">
          <span className="text-muted-foreground">Requested ship date</span>
          <span className="text-right">
            <span className="block font-medium">
              {draft.requestedShippingDate ?? 'Unavailable'}
            </span>
            <span className="block text-xs text-muted-foreground">
              {draft.requestedDatesSource === 'approval'
                ? 'Approved for D365'
                : 'Customer PO extraction'}
            </span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2">
          <span className="text-muted-foreground">Requested delivery date</span>
          <span className="text-right">
            <span className="block font-medium">
              {draft.requestedDeliveryDate ?? 'Unavailable'}
            </span>
            <span className="block text-xs text-muted-foreground">
              {draft.requestedDatesSource === 'approval'
                ? 'Approved for D365'
                : 'Customer PO reference'}
            </span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2">
          <span className="text-muted-foreground">PO payment terms</span>
          <span className="font-medium">{draft.paymentTermsCode ?? 'Unavailable'}</span>
        </div>
        <div className="space-y-2 rounded-md border bg-muted/20 px-3 py-2">
          <div className="flex items-center justify-between gap-3">
            <label className="text-muted-foreground" htmlFor={`payment-terms-${draft.id}`}>
              D365 payment terms
            </label>
            <select
              id={`payment-terms-${draft.id}`}
              className="max-w-[18rem] rounded-md border bg-background px-2 py-1 text-right font-medium"
              value={selectedPaymentTerms}
              onChange={(event) => onPaymentTermsChange(event.target.value)}
            >
              {!selectedPaymentTerms ? <option value="">Use D365 default</option> : null}
              {paymentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="text-xs text-muted-foreground">
            Resolved by validation: {draft.d365ResolvedPaymentTermsCode ?? 'not available'};
            customer fallback: {draft.customerDefaultPaymentTermsCode ?? 'not available'}.
          </div>
          {paymentOverrideActive ? (
            <Textarea
              value={paymentOverrideReason}
              onChange={(event) => onPaymentOverrideReasonChange(event.target.value)}
              placeholder="Why is this override authorized? (required)"
              className="min-h-16"
            />
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2">
          <span className="text-muted-foreground">Shipping site</span>
          <span className="font-medium">{draft.shippingSiteId ?? 'Unavailable'}</span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2">
          <span className="text-muted-foreground">Owner</span>
          <span className="text-right">
            {draft.owner ? (
              <>
                <div className="font-medium">{draft.owner.email}</div>
                <div className="text-xs text-muted-foreground">
                  {draft.owner.source === 'd365'
                    ? 'from D365'
                    : draft.owner.source === 'email_recipient'
                      ? 'from email recipient'
                      : 'from forwarder'}
                </div>
              </>
            ) : (
              <span className="text-muted-foreground">Unassigned</span>
            )}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2">
          <span className="text-muted-foreground">Total value</span>
          <span className="font-medium">{formatCurrency(draft.totalValue)}</span>
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-sm font-medium">Lines</div>
        {draft.lineItems.length > 0 ? (
          <div className="space-y-2">
            {draft.lineItems.map((line) => (
              <div
                key={line.lineNumber}
                className="rounded-md border bg-background px-3 py-2 text-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium">
                    Line {line.lineNumber}: {line.itemCode ?? 'Item pending'}
                  </div>
                  <div className="text-muted-foreground">
                    {line.quantity ?? '-'} {line.unitOfMeasure ?? ''}
                  </div>
                </div>
                <div className="mt-1 text-muted-foreground">
                  {line.description ?? 'No description'}
                </div>
                <div className="mt-1 flex flex-wrap items-start justify-between gap-2 text-xs">
                  <div className="flex flex-wrap gap-x-2 text-muted-foreground">
                    <span>
                      {line.poUnitPrice !== null ? (
                        <span
                          className={
                            line.priceStatus === 'mismatch' ? 'font-medium text-destructive' : ''
                          }
                        >
                          PO unit price: {line.poUnitPrice.toFixed(2)}
                          {line.poUnitPriceCurrency ? ` ${line.poUnitPriceCurrency}` : ''}
                        </span>
                      ) : (
                        <span>PO unit price: not extracted</span>
                      )}
                    </span>
                    <span>
                      {line.salesPrice !== null ? (
                        <span
                          className={
                            line.priceStatus === 'mismatch'
                              ? 'font-medium text-destructive'
                              : 'font-medium text-foreground'
                          }
                        >
                          D365 agreement: {line.salesPrice.toFixed(2)}
                          {line.salesPriceCurrency ? ` ${line.salesPriceCurrency}` : ''}
                        </span>
                      ) : (
                        <span>
                          {line.priceStatus === 'd365_missing'
                            ? 'No active D365 trade agreement'
                            : line.priceStatus === 'pending'
                              ? 'D365 agreement: check pending'
                              : 'D365 agreement: unavailable'}
                        </span>
                      )}
                    </span>
                  </div>
                  {line.lineTotal !== null ? (
                    <div className="text-muted-foreground">
                      PO total: {formatCurrency(line.lineTotal)}
                    </div>
                  ) : null}
                </div>
                {line.priceReviewMessage ? (
                  <div className="mt-1 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1 text-xs">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                    <span className="text-amber-900/80">{line.priceReviewMessage}</span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-md border bg-background px-3 py-2 text-sm text-muted-foreground">
            No line details available.
          </div>
        )}
      </div>
    </div>
  );
}
